'use client';

import {Icon} from '@components/@icons';
import {MeinText} from '@components/@typography/MeinText';
import {Easing} from '@config/easing';
import {useUpdateEffect} from '@hooks/useUpdateEffect';
import {
    FormControl,
    FormGroup,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
} from '@mui/material';
import {unfocusActiveElement} from '@utils/unfocusActiveElement';
import wait from '@utils/wait';
import classNames from 'classnames';
import {AnimatePresence, motion} from 'framer-motion';
import {ForwardedRef, ReactNode, forwardRef, useState} from 'react';
import {AnimatedFormHelperText} from '..';
import {MeinScrollbarForMui} from '../Scrollbar/MeinScrollbarForMui';
import styles from './MeinSelectMulti.module.css';

interface IProps<T> {
    options: T[];
    value?: T[] | undefined;
    label?: string;
    error?: boolean;
    helperText?: string;
    placeholder?: string;
    className?: string;
    onChange: (selectedOptions: T[]) => void;
    onClose?: () => void;
    getOptionLabel?: (option: T) => string | number;
    getOptionValue?: (option: T) => string | number | undefined;
    variant?: 'solid' | 'outline';
    size?: 'small' | 'medium';
    showClearIcon?: boolean;
    renderValue?: (selectedOptions: any[]) => ReactNode;
}

export const MeinSelectMulti = forwardRef(function MeinSelectMulti<T>(
    {
        options,
        value: selectedOptions = [],
        label,
        error,
        helperText,
        className,
        onChange,
        onClose,
        getOptionLabel = (option: T) => option as string,
        getOptionValue = (option: T) => option as string,
        variant = 'outline',
        size = 'medium',
        placeholder,
        showClearIcon,
        renderValue: renderValueProp,
        ...rest
    }: IProps<T>,
    ref: ForwardedRef<HTMLInputElement>,
) {
    const [isOpen, setIsOpen] = useState(false);
    const [memoizedOptions, setMemoizedOptions] = useState(options);

    const renderOption = (option: T) => {
        const isChecked = !!selectedOptions?.find(
            selectedOption =>
                getOptionValue(selectedOption) === getOptionValue(option),
        );

        return (
            <>
                <motion.div
                    className={styles.itemIconContainer}
                    animate={
                        !isChecked
                            ? {
                                  scale: [0.5, 1],
                                  transition: {
                                      type: 'tween',
                                      ease: Easing.bezier.outNormal,
                                  },
                              }
                            : {
                                  scale: [1.2, 1],
                                  transition: {
                                      type: 'spring',
                                      mass: 0.3,
                                      damping: 7,
                                      stiffness: 200,
                                  },
                              }
                    }
                >
                    {isChecked ? (
                        <Icon name="check-square" variant="solid" />
                    ) : (
                        <Icon name="square" />
                    )}
                </motion.div>
                <MeinText type="label" size="m" tag="span">
                    {getOptionLabel(option)}
                </MeinText>
            </>
        );
    };

    const renderValue = (
        selectedValues?: ReturnType<typeof getOptionValue>[],
    ) => {
        return (
            <span className={styles.selectValue}>
                {placeholder || label}
                <AnimatePresence>
                    {!!selectedValues?.length && (
                        <motion.span
                            initial={{
                                width: 0,
                                opacity: 0,
                                marginLeft: '-0.38rem',
                            }}
                            animate={{width: 'auto', opacity: 1, marginLeft: 0}}
                            exit={{
                                width: 0,
                                opacity: 0,
                                marginLeft: '-0.38rem',
                            }}
                            className={styles.selectionSize}
                        >
                            {selectedValues.length}
                        </motion.span>
                    )}
                </AnimatePresence>
            </span>
        );
    };

    const handleChange = (
        e: SelectChangeEvent<ReturnType<typeof getOptionValue>[]>,
    ) => {
        const value = e.target.value;
        let selectedOptions: T[] = [];
        const getOptionFromValues = (values: (string | number | undefined)[]) =>
            values.map(value => {
                return memoizedOptions.find(
                    option => getOptionValue(option) === value,
                )!;
            });
        if (typeof value === 'string') {
            // browser autofill?
            selectedOptions = getOptionFromValues(value.split(','));
        } else {
            // user input
            selectedOptions = getOptionFromValues(value);
        }
        onChange(selectedOptions);
    };

    const clearSelections = () => {
        onChange([]);
    };

    const handleClose = async () => {
        onClose?.();
        unfocusActiveElement();
        await wait(250);
        setIsOpen(false);
    };

    const handleOpen = () => {
        setIsOpen(true);
    };

    useUpdateEffect(() => {
        !isOpen && setMemoizedOptions(options);
    }, [isOpen, options]);

    return (
        <FormGroup>
            <FormControl>
                {variant === 'outline' && (
                    <InputLabel shrink className={styles.label}>
                        {label}
                    </InputLabel>
                )}
                <Select
                    disabled={!options.length}
                    notched={variant === 'outline'}
                    label={variant === 'outline' ? label : undefined}
                    multiple
                    displayEmpty
                    ref={ref}
                    onChange={handleChange}
                    onClose={handleClose}
                    onOpen={handleOpen}
                    value={selectedOptions?.map(getOptionValue)}
                    renderValue={renderValueProp || renderValue}
                    error={error}
                    className={classNames(
                        styles.select,
                        styles[size],
                        styles[variant],
                        {
                            [styles.hasValue]: !!selectedOptions?.length,
                        },
                        className,
                    )}
                    MenuProps={{
                        slotProps: {
                            paper: {component: MeinScrollbarForMui},
                        },
                        transitionDuration: 250,
                        PopoverClasses: {
                            paper: styles.menu,
                        },
                        anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                        },
                        transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                        },
                    }}
                    IconComponent={() => (
                        <InputAdornment
                            className={styles.adornment}
                            position="end"
                        >
                            {(!showClearIcon || !selectedOptions?.length) && (
                                <Icon
                                    name="chevron-down"
                                    size={22}
                                    className={styles.chevron}
                                />
                            )}
                            {showClearIcon && !!selectedOptions?.length && (
                                <IconButton
                                    className={styles.clearButton}
                                    onClick={clearSelections}
                                >
                                    <Icon name="close" />
                                </IconButton>
                            )}
                        </InputAdornment>
                    )}
                    {...rest}
                >
                    {memoizedOptions.map(option => {
                        return (
                            <MenuItem
                                disableRipple
                                key={getOptionValue(option)}
                                value={getOptionValue(option)}
                                className={styles.menuItem}
                            >
                                {renderOption(option)}
                            </MenuItem>
                        );
                    })}
                </Select>
            </FormControl>
            <AnimatedFormHelperText error={error} helperText={helperText} />
        </FormGroup>
    );
});
